import { useEffect, useState } from "react";
import { Form, FormAutoComplete, FormInput, FormSelect, FormActions } from "../Controls/Form";
import UserData from "../Controls/UserData";
import Helpers from "../Controls/helpers";
import { toast } from "react-toastify";
import { useAccountProfileQuery } from "../../query/useAccountProfile";

function emptyLocation() {
  return {
    ID: 0,
    Name: "",
    CountryID: null,
    CountryName: "",
    StateID: null,
    StateName: "",
    CityID: null,
    CityName: "",
    Address1: "",
    Address2: "",
    PostalCode: "",
    MinAnnualCompensation: null,
  };
}

function countryList(lookups) {
  return lookups?.Countries || lookups?.countries || [];
}

function findLocation(profile, id) {
  if (!id) return null;
  return (profile?.locations || []).find((l) => Number(l.ID) === id) || null;
}

/**
 * Location form body. Prefer LocationEditModal for UI entry points.
 * @param {number} locationId - 0 for new, otherwise existing PersonLocation.ID
 * @param {() => void} onClose - called after save/delete/cancel
 */
export default function LocationEdit({ locationId = 0, onClose }) {
  const id = Number.parseInt(locationId, 10) || 0;
  const { data: profile } = useAccountProfileQuery();
  const [lookups] = useState(UserData.Lookups() || {});
  const [location, updateLocation] = useState(() => emptyLocation());
  const [states, updateStates] = useState([]);
  const [cities, updateCities] = useState([]);
  const [originalLocation, setOriginalLocation] = useState(() => emptyLocation());

  useEffect(() => {
    if (!id) {
      updateLocation(emptyLocation());
      setOriginalLocation(emptyLocation());
      return;
    }
    const found = findLocation(profile, id);
    if (found) {
      updateLocation({ ...found });
      setOriginalLocation({ ...found });
    }
  }, [id, profile]);

  function close() {
    onClose?.();
  }

  async function onDoneAsync() {
    await UserData.UpdateAsync("locations", location, true);
    toast.success("Location Saved");
    close();
  }

  async function getCountryStatesAsync(countryId, { preserveCity = false } = {}) {
    const idNum = Number(countryId) || 0;
    if (!idNum) return;

    const countries = countryList(lookups);
    const country = countries.find((l) => Number(l.Key) === idNum);
    const nextStates = await Helpers.StatesAsync(idNum);

    let locPatch = {
      CountryID: idNum,
      CountryName: country?.Value || "",
      StateID: null,
      StateName: "",
      CityID: null,
      CityName: "",
    };

    if (Number(originalLocation?.CountryID) === idNum && originalLocation.StateID) {
      const st = nextStates.find((s) => Number(s.ID) === Number(originalLocation.StateID));
      if (st) {
        locPatch = {
          ...locPatch,
          StateID: st.ID,
          StateName: st.Name,
          ...(preserveCity
            ? {
                CityID: originalLocation.CityID || null,
                CityName: originalLocation.CityName || "",
              }
            : {}),
        };
        if (preserveCity && st.ID) {
          const nextCities = await Helpers.CitiesAsync(st.ID);
          updateCities(nextCities);
        } else {
          updateCities([]);
        }
      } else {
        updateCities([]);
      }
    } else {
      updateCities([]);
    }

    updateLocation((prev) => ({ ...prev, ...locPatch }));
    updateStates(Array.isArray(nextStates) ? nextStates : []);
  }

  useEffect(() => {
    if (location?.CountryID) {
      getCountryStatesAsync(location.CountryID, { preserveCity: true });
    }
    // Load country/state options once when opening an existing location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onChangeCountry(value) {
    const countryId = Number(value?.ID ?? value?.id ?? value) || 0;
    if (countryId > 0) {
      getCountryStatesAsync(countryId, { preserveCity: false });
    }
  }

  async function onChangeStateAsync(value) {
    const stateId = Number(value?.ID ?? value?.id ?? value) || 0;
    if (!(stateId > 0)) return;

    const fromList = states.find((s) => Number(s.ID) === stateId);
    const stateName = value?.Name ?? value?.name ?? fromList?.Name ?? "";
    const nextCities = await Helpers.CitiesAsync(stateId);

    let locPatch = {
      StateID: stateId,
      StateName: stateName,
      CityID: null,
      CityName: "",
    };
    if (Number(originalLocation?.StateID) === stateId && originalLocation.CityName) {
      const city = (nextCities || []).find((c) => c.Name === originalLocation.CityName);
      if (city) locPatch = { ...locPatch, CityID: city.ID, CityName: city.Name };
    }
    updateLocation((prev) => ({ ...prev, ...locPatch }));
    updateCities(Array.isArray(nextCities) ? nextCities : []);
  }

  function onDelete() {
    UserData.DeleteAsync("locations", location.ID).then(() => {
      toast.success("Location Deleted!");
      close();
    });
  }

  function onOpenStates() {
    if (location.CountryID) {
      onChangeCountry(location.CountryID);
    }
  }

  const countries = countryList(lookups).map((c) => ({
    ID: c.Key,
    Name: c.Value,
  }));

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={location} onUpdate={updateLocation}>
        <FormInput label="Location Name" field="Name" />
        <FormSelect
          label="Country"
          field="CountryID"
          nameField="CountryName"
          options={countries}
          labelProp="Name"
          valueProp="ID"
          onChange={onChangeCountry}
        />
        <div className="cm-form-row cm-form-row--half">
          <FormSelect
            label="State"
            field="StateID"
            nameField="StateName"
            options={states}
            labelProp="Name"
            valueProp="ID"
            onOpen={onOpenStates}
            onChange={onChangeStateAsync}
          />
          <FormInput label="Zip / Postal Code" field="PostalCode" />
        </div>
        <FormAutoComplete label="City" field="CityName" options={cities.map((c) => c.Name)} />
        <FormInput
          label="Annual income you need here"
          field="MinAnnualCompensation"
          type="number"
        />
        <FormInput label="Address Line 1" field="Address1" />
        <FormInput label="Address Line 2" field="Address2" />
        <FormActions
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            location.ID > 0
              ? async () => {
                  onDelete();
                }
              : undefined
          }
        />
      </Form>
    </div>
  );
}
